import { z } from 'zod';
import { ChannelId } from './common.js';

/**
 * What can cause a payout.
 *
 * A CLOSED, TYPED set — deliberately not a rules engine. Their
 * `TriggerConditions` was already drifting toward one, and a general rules
 * engine is a thing you then have to secure, version, debug and explain. When
 * Twitch adds a field, we add a field.
 *
 * Per-provider, because the sets genuinely differ: YouTube has no
 * channel-points analogue, and pretending otherwise with a generic shape would
 * mean every consumer handling a case that cannot occur.
 */
export const TWITCH_TRIGGER_TYPES = [
  'subscription', 'resub', 'gift_sub', 'channel_points', 'bits', 'follow', 'raid', 'chat_command',
] as const;

export type TwitchTriggerType = (typeof TWITCH_TRIGGER_TYPES)[number];

/**
 * Conditions, all optional and all AND-ed.
 *
 * Absent means "do not care" — never "must be zero". Theirs conflated the two
 * in places, which is how a trigger configured for any tier stopped matching
 * tier 1.
 */
export const TriggerConditions = z.object({
  /** Twitch tier strings, not numbers: '1000' | '2000' | '3000'. */
  tier: z.enum(['1000', '2000', '3000']).optional(),
  min_months: z.number().int().min(1).optional(),
  min_gift_count: z.number().int().min(1).optional(),
  min_bits: z.number().int().min(1).optional(),
  /** The Twitch-side custom reward id. */
  channel_points_reward_id: z.string().optional(),
  min_raid_viewers: z.number().int().min(1).optional(),
  chat_command: z.string().min(1).max(40).optional(),
  /** Anonymous gifters are tracked but not rewarded, unless a trigger opts in. */
  allow_anonymous: z.boolean().optional(),
});

export const TriggerPayouts = z.object({
  currency: z.array(z.object({ code: z.string(), amount: z.number().int().min(1) })).max(3).default([]),
  reward_ids: z.array(z.string()).max(20).default([]),
  bundle_ids: z.array(z.string()).max(5).default([]),
});

/**
 * A trigger: platform event -> payout.
 *
 * `types` is DERIVED server-side from `matches`, and the API rejects it as
 * input.
 *
 * L17 — Firestore cannot index into `matches[].type`, so the query runs on a
 * denormalised array. An event saved without it is invisible to that query and
 * SILENTLY NEVER FIRES. Their rule was "every writer must set it", which is a
 * rule one writer eventually forgets. Deriving it means no writer is allowed
 * to supply it, so none can forget.
 */
export const Trigger = z.object({
  id: z.string(),
  channel_id: ChannelId,
  name: z.string().min(1).max(120),
  provider: z.literal('twitch'),
  matches: z
    .array(z.object({ type: z.enum(TWITCH_TRIGGER_TYPES), conditions: TriggerConditions.default({}) }))
    .min(1)
    .max(10),
  /** Derived. Present on reads, refused on writes. */
  types: z.array(z.enum(TWITCH_TRIGGER_TYPES)).readonly(),
  payouts: TriggerPayouts,
  enabled: z.boolean().default(true),
  /** Stamped so a payout whose meaning changed cannot be misread (L21). */
  payouts_version: z.number().int().min(1).default(1),
});

/** The single deriver. Nothing else computes this. */
export function deriveTriggerTypes(
  matches: readonly { type: TwitchTriggerType }[]
): TwitchTriggerType[] {
  return [...new Set(matches.map((m) => m.type))];
}

export type Trigger = z.infer<typeof Trigger>;
export type TriggerConditions = z.infer<typeof TriggerConditions>;
export type TriggerPayouts = z.infer<typeof TriggerPayouts>;
